import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { loadUkSubunits, UK_NATIONS } from "./ukSubunits";

// Natural Earth 110m country polygons (keyless, CORS-enabled via jsDelivr).
// Carries Swedish names (NAME_SV), CONTINENT and an ISO_A2_EH fallback for the
// handful of features where ISO_A2 is "-99" (France, Norway, Kosovo, …).
const GEOJSON_URL =
  "https://cdn.jsdelivr.net/gh/nvkelso/natural-earth-vector@v5.1.2/geojson/ne_110m_admin_0_countries.geojson";

export interface Country {
  code: string; // ISO 3166-1 alpha-2 (matches Photon's countrycode)
  name: string;
  continent: string;
}

export interface MapPlace {
  name: string;
  lat: number;
  lng: number;
  date?: string; // free-text "when I was here"
}

// Not sovereign countries — excluded from the picker and the world/continent
// totals. Antarctica (AQ) is kept as a single countable entry so it can be
// marked visited even though it has no sovereign states.
export const NON_COUNTRY = new Set(["Seven seas (open ocean)"]);

// ISO alpha-2 for a feature, falling back to ISO_A2_EH where ISO_A2 is "-99".
export const codeOf = (p: any): string =>
  p.ISO_A2 && p.ISO_A2 !== "-99" ? p.ISO_A2 : p.ISO_A2_EH;
export const nameOf = (p: any): string => p.NAME_SV || p.NAME || p.ADMIN || "";

const escapeHtml = (s: string) =>
  s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));

// France's Natural Earth polygon bundles French Guiana (South America) into the
// same feature as metropolitan France, so a "visited FR" fill would paint Guiana
// green in South America too. Drop France's far-western (American) sub-polygons;
// metropolitan France + Corsica sit east of this longitude.
const OVERSEAS_MIN_LNG = -20;
const stripFrenchOverseas = (data: any): any => {
  data.features.forEach((f: any) => {
    if (codeOf(f.properties) !== "FR" || f.geometry?.type !== "MultiPolygon") return;
    f.geometry.coordinates = f.geometry.coordinates.filter((poly: number[][][]) =>
      poly[0].some((c: number[]) => c[0] > OVERSEAS_MIN_LNG)
    );
  });
  return data;
};

// Fetch once and share across mounts (the file is ~820 KB).
let geojsonCache: Promise<any> | null = null;
export const loadCountries = () => loadGeojson();
const loadGeojson = () => {
  if (!geojsonCache) {
    geojsonCache = fetch(GEOJSON_URL)
      .then((r) => {
        if (!r.ok) throw new Error("Kunde inte ladda kartdata");
        return r.json();
      })
      .then(stripFrenchOverseas);
  }
  return geojsonCache;
};

// The list of countable countries: base map minus the single GB polygon, plus
// the four UK constituent countries. Shared by the map's onData and the stats
// (so totals are available even when only the globe view is mounted).
const buildCountryList = (data: any): Country[] => {
  const seen = new Set<string>();
  const list: Country[] = [];
  data.features.forEach((f: any) => {
    const code = codeOf(f.properties);
    const continent = f.properties.CONTINENT;
    if (!code || code === "-99" || code === "GB" || NON_COUNTRY.has(continent) || seen.has(code)) return;
    seen.add(code);
    list.push({ code, name: nameOf(f.properties), continent });
  });
  Object.values(UK_NATIONS).forEach((n) =>
    list.push({ code: n.code, name: n.name, continent: n.continent })
  );
  list.sort((a, b) => a.name.localeCompare(b.name, "sv"));
  return list;
};
export const loadCountryList = async (): Promise<Country[]> => buildCountryList(await loadCountries());

const VISITED = "#3aa76d";
const VISITED_LINE = "#2e8c5a";
const UNVISITED_FILL = "rgba(26,26,26,0.03)";
const UNVISITED_LINE = "rgba(26,26,26,0.25)";

export const WorldMap = ({
  visited,
  places = [],
  onData,
}: {
  visited: string[]; // ISO-2 codes of countries to fill (derived from places)
  places?: MapPlace[];
  onData?: (countries: Country[]) => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const geoRef = useRef<L.GeoJSON | null>(null);
  const ukRef = useRef<L.GeoJSON | null>(null);
  const pinsRef = useRef<L.LayerGroup | null>(null);
  const visitedRef = useRef<Set<string>>(new Set(visited));

  const styleByCode = (code: string): L.PathOptions => {
    const on = visitedRef.current.has(code);
    return {
      fillColor: on ? VISITED : UNVISITED_FILL,
      fillOpacity: on ? 0.85 : 1,
      color: on ? VISITED_LINE : UNVISITED_LINE,
      weight: on ? 1 : 0.5,
    };
  };
  const styleFor = (feature: any): L.PathOptions => styleByCode(codeOf(feature.properties));
  const styleUk = (feature: any): L.PathOptions => styleByCode(feature.properties.code);

  // init map + load polygons once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      scrollWheelZoom: true,
      worldCopyJump: true,
      minZoom: 1,
      attributionControl: false,
    }).setView([25, 5], 2);
    mapRef.current = map;
    // Dedicated pane so place pins always sit above the country/UK polygons,
    // regardless of which layer finishes loading last.
    map.createPane("pinsPane");
    map.getPane("pinsPane")!.style.zIndex = "650";
    pinsRef.current = L.layerGroup().addTo(map);

    // Esri's Light Gray Canvas — a keyless light basemap (CARTO now requires an
    // API key and stamps "API KEY REQUIRED" over its tiles).
    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
      { maxZoom: 16, attribution: "Tiles &copy; Esri" }
    ).addTo(map);

    let cancelled = false;
    Promise.all([loadGeojson(), loadUkSubunits().catch(() => null)])
      .then(([data, uk]) => {
        if (cancelled || !mapRef.current) return;
        // Base countries — but drop the single UK polygon; its four constituent
        // countries are drawn from the subunits overlay instead.
        const geo = L.geoJSON(data, {
          filter: (f: any) => codeOf(f.properties) !== "GB",
          style: styleFor as any,
          onEachFeature: (feature, layer) =>
            layer.bindTooltip(nameOf(feature.properties), { sticky: true }),
        }).addTo(map);
        geoRef.current = geo;
        geo.bringToBack();

        if (uk) {
          const ukLayer = L.geoJSON(uk, {
            style: styleUk as any,
            onEachFeature: (feature, layer) =>
              layer.bindTooltip(feature.properties.name, { sticky: true }),
          }).addTo(map);
          ukRef.current = ukLayer;
        }

        if (onData) onData(buildCountryList(data));
        setTimeout(() => map.invalidateSize(), 0);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      map.remove();
      mapRef.current = null;
      geoRef.current = null;
      ukRef.current = null;
      pinsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // restyle whenever the visited set changes
  useEffect(() => {
    visitedRef.current = new Set(visited);
    if (geoRef.current) geoRef.current.setStyle(styleFor as any);
    if (ukRef.current) ukRef.current.setStyle(styleUk as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visited]);

  // redraw place pins whenever the list changes
  const placeSig = places.map((p) => `${p.lat},${p.lng}:${p.name}:${p.date || ""}`).join("|");
  useEffect(() => {
    const layer = pinsRef.current;
    if (!layer) return;
    layer.clearLayers();
    places.forEach((p) => {
      L.circleMarker([p.lat, p.lng], {
        pane: "pinsPane",
        radius: 4,
        color: "#1a1a1a",
        weight: 1.5,
        fillColor: "#e2b31a",
        fillOpacity: 1,
      })
        // click/tap shows the place + which year(s) I was there (one box only)
        .bindPopup(
          `<b>${escapeHtml(p.name)}</b>${p.date ? `<br>📅 ${escapeHtml(p.date)}` : "<br><i>Inget år angivet</i>"}`
        )
        .addTo(layer);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeSig]);

  return <div className="places__map" ref={containerRef} />;
};
